import { isFunction } from 'lodash';

import { Button } from '@/components/button';
import { Icon } from '@/components/icon';
import { STRIPED_STYLE } from '@/components/modules/route/bar-mini';
import { tv } from '@/utils/styles';

interface Props {
  children: React.ReactNode | React.ReactNode[];
  onClickDetails?: () => void;
  isPredicted?: boolean;
  actionButtonClassName?: string;
}

const JourneyRowTemplate = ({ children, onClickDetails, isPredicted, actionButtonClassName }: Props) => {
  const hasClickEvent = onClickDetails && isFunction(onClickDetails);

  const handleRowClick = () => {
    if (hasClickEvent) {
      onClickDetails();
    }
  };

  const handleMoreDetailsClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (hasClickEvent) {
      onClickDetails();
    }
  };

  const handleClick = () => {
    return handleRowClick();
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'enter' || e.key === 'space') {
      handleClick();
    }
  };

  const { row, actionButtonContainer, predicted, content } = styles({
    isPredicted,
    isClickable: hasClickEvent,
  });

  return (
    <div role="button" tabIndex={0} onKeyUp={handleKeyUp} className={row()} onClick={handleClick}>
      {isPredicted && (
        <div style={STRIPED_STYLE} className={predicted()}>
          Predicted
        </div>
      )}
      <div className={content()}>{children}</div>
      <div className={actionButtonContainer({ className: actionButtonClassName })}>
        {hasClickEvent && (
          <Button size="small" onClick={handleMoreDetailsClick}>
            <Icon size="4" name="arrow-right-alt" color="white" />
          </Button>
        )}
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    row: 'group relative flex w-full cursor-default flex-col gap-8 rounded-md bg-white px-2 py-4 outline outline-1 outline-grey-200 sm:px-2 md:flex-row md:items-center md:px-8',
    content: 'z-5 w-full items-center text-left text-baseSm text-text-primary',
    actionButtonContainer: 'bottom-4 right-md top-4 z-5 hidden content-end items-center self-end md:flex md:self-auto',
    predicted:
      'absolute -bottom-px -left-1 -top-px rotate-180 rounded-br-md rounded-tr-md border-b border-r border-t border-green-200 bg-green-200 !bg-[length:4px] bg-repeat text-center text-xxs font-medium uppercase text-green-500 vertical-lr group-hover:border-green-500',
  },
  variants: {
    isPredicted: {
      true: {
        row: 'outline-green-200 hover:outline-green-500',
      },
    },
    isClickable: {
      true: {
        row: 'cursor-pointer hover:shadow-card hover:outline-grey-400',
      },
    },
  },
});

export { JourneyRowTemplate };
